import { Link, NavLink, Outlet } from 'react-router-dom';

import { config } from '@/app/config';
import { Button, Icon, LinkButton, Logo } from '@/components/ui';
import { useAuth } from '@/hooks/useAuth';
import { dashboardFor, paths } from '@/routes/paths';
import { cn } from '@/utils/cn';

const NAV = [
  { to: paths.internships, label: 'Internships' },
  { to: `${paths.home}#for-students`, label: 'For students' },
  { to: `${paths.home}#for-companies`, label: 'For companies' },
  { to: `${paths.home}#pricing`, label: 'Pricing' },
  { to: `${paths.home}#faq`, label: 'FAQ' },
];

/** Marketing and authentication shell. */
export function PublicLayout() {
  const { isAuthenticated, isImpersonating, user, signOut } = useAuth();
  // Until the email is confirmed the code page is the only way on, so there is
  // no dashboard to offer.
  const canOpenDashboard = Boolean(user?.is_email_verified) || isImpersonating;

  const homeForRole = dashboardFor(user?.role);

  return (
    <div className="bg-surface flex min-h-screen flex-col">
      <header className="border-line bg-surface/95 sticky top-0 z-40 border-b backdrop-blur">
        <div className="app-container flex h-16 items-center justify-between gap-4 lg:gap-6">
          <Link
            to={paths.home}
            className="text-ink flex shrink-0 items-center gap-2 text-base font-semibold tracking-tight whitespace-nowrap sm:gap-2.5 sm:text-lg"
          >
            {/* The same brand tile as the portal sidebar: the Wonder mark on
                the brand's night. Hidden from screen readers -- the name is
                written beside it. */}
            <span
              aria-hidden="true"
              className="bg-night text-on-night grid size-9 shrink-0 place-items-center rounded-lg"
            >
              <Logo size={20} />
            </span>
            {config.appName}
          </Link>

          {/* Tighter between md and lg: five links, the brand and two buttons
              overran a 768px screen by a few pixels at the wider spacing. */}
          <nav className="hidden items-center gap-4 md:flex lg:gap-6" aria-label="Main">
            {NAV.map((item) => (
              <NavLink
                key={item.label}
                to={item.to}
                className={({ isActive }) =>
                  cn(
                    'text-sm transition',
                    isActive ? 'text-ink font-medium' : 'text-ink-muted hover:text-ink',
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex shrink-0 items-center gap-1 sm:gap-2">
            {isAuthenticated ? (
              <>
                {/* The signed-in twin of "Get started": the one place to go
                    from here, so it carries the same filled weight. */}
                {canOpenDashboard && (
                  <LinkButton to={homeForRole} size="sm" className="group">
                    <Icon name="dashboard" size={15} className="hidden sm:block" />
                    Dashboard
                    <Icon
                      name="arrowRight"
                      size={14}
                      className="hidden transition-transform group-hover:translate-x-0.5 sm:block"
                    />
                  </LinkButton>
                )}
                <Button variant="ghost" size="sm" onClick={() => void signOut()}>
                  Sign out
                </Button>
              </>
            ) : (
              <>
                <LinkButton to={paths.login} variant="ghost" size="sm">
                  Log in
                </LinkButton>
                <LinkButton
                  to={paths.registerStudent}
                  size="sm"
                  className="bg-night hover:bg-night-raised shadow-night/25 hover:shadow-night/30"
                >
                  Get started
                </LinkButton>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <Footer />
    </div>
  );
}

function Footer() {
  return (
    <footer className="border-line bg-surface-muted border-t">
      <div className="app-container grid gap-8 py-12 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <p className="text-brand-600 text-lg font-semibold">{config.appName}</p>
          <p className="text-ink-muted mt-2 text-sm">
            AI-powered internship and recruitment platform connecting students with companies.
          </p>
        </div>

        <FooterColumn
          title="Students"
          links={[
            { to: paths.internships, label: 'Browse internships' },
            { to: paths.registerStudent, label: 'Create an account' },
            { to: `${paths.home}#how-it-works`, label: 'How it works' },
          ]}
        />
        <FooterColumn
          title="Companies"
          links={[
            { to: paths.registerCompany, label: 'Hire an intern' },
            { to: `${paths.home}#for-companies`, label: 'Why Join-Wonder' },
            { to: `${paths.home}#pricing`, label: 'Pricing' },
          ]}
        />
        <FooterColumn
          title="Company"
          links={[
            { to: `${paths.home}#contact`, label: 'Contact' },
            { to: `${paths.home}#blog`, label: 'Blog' },
            { to: `${paths.home}#faq`, label: 'FAQ' },
          ]}
        />
      </div>

      <div className="border-line border-t">
        <div className="app-container text-ink-muted flex flex-col gap-3 py-6 text-sm sm:flex-row sm:items-center sm:justify-between">
          <p>
            © {new Date().getFullYear()} {config.appName}. All rights reserved.
          </p>
          <div className="flex gap-4">
            <Link to={`${paths.home}#legal`} className="hover:text-ink">
              Privacy
            </Link>
            <Link to={`${paths.home}#legal`} className="hover:text-ink">
              Terms
            </Link>
            <a
              href="https://www.linkedin.com"
              target="_blank"
              rel="noreferrer noopener"
              className="hover:text-ink"
            >
              LinkedIn
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterColumn({ title, links }) {
  return (
    <div>
      <p className="text-ink text-sm font-semibold">{title}</p>
      <ul className="mt-3 space-y-2">
        {links.map((link) => (
          <li key={link.label}>
            <Link to={link.to} className="text-ink-muted hover:text-ink text-sm">
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
